import Attachment16 from '@carbon/icons-react/es/attachment/16';
import Download16 from '@carbon/icons-react/es/download/16';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { Tooltip } from 'antd';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectId, TaskId } from 'api/models';
import { ProjectTaskAttachments } from 'api/project-files/types';
import { Button } from 'components/atoms/button';
import { Collapse } from 'components/atoms/collapse';
import { Panel } from 'components/atoms/collapse/panel';
import { Grid } from 'components/atoms/grid';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslation } from 'hooks/use-translation';
import { useUserNameGetter } from 'hooks/user-user-name';
import { handleDownload } from 'utils/attachment';
import { FileNameRenderer } from './file-groups';

const Title = styled.div`
  display: flex;
  align-items: center;
  color: var(--gray-80);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-05);

  svg {
    margin-right: var(--spacing-04);
  }
`;

type Row = {
  id: number;
  name: string;
  created_on: string;
};

const StyledGrid = styled(Grid)`
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
    padding-top: 30px !important;
    padding-left: var(--spacing-08);
  }

  .ag-row {
    .file-more-options {
      visibility: hidden;
    }
    &:hover {
      .file-more-options {
        visibility: visible;
      }
    }
  }

  .ag-react-container > :not(:first-child) {
    display: none !important;
  }
`;

const GridWrapper = styled.div`
  margin-top: var(--layout-01);

  .ag-layout-auto-height .ag-center-cols-clipper {
    min-height: 80px;
  }

  .ag-overlay-wrapper.ag-layout-auto-height.ag-overlay-no-rows-wrapper {
    justify-content: start;
    padding-top: 30px;
    padding-left: var(--spacing-08);
  }

  display: ${(props: { rowCount: number | null }) =>
    props.rowCount === null || props.rowCount > 0 ? 'block' : 'none'};
`;

const GridCollapse = styled(Collapse)`
  .ant-collapse-header {
    padding-right: 0 !important;
  }

  .ant-collapse-item-active {
    padding-bottom: 0 !important;
  }
`;

const TaskNameButton = styled(Button)`
  padding-left: 0;
`;

const TaskCellRenderer = ({ data }) => {
  const navigateToTask = useNavigateToTask();
  const onClick = useCallback(() => {
    navigateToTask(TaskId(data.projectId, data.taskId));
  }, [data.projectId, data.taskId, navigateToTask]);

  return (
    <TaskNameButton type="link" size="small" onClick={onClick}>
      {data.source}
    </TaskNameButton>
  );
};

const DownloadButtonRenderer = ({ data }) => {
  const downloadAttachmentErrorText = useTranslation(
    'attachments.download.error',
  );

  return (
    <Tooltip title={<FormattedMessage id="download" />} placement="left">
      <Button
        icon={<Download16 />}
        type="text"
        size="small"
        onClick={() => handleDownload(data.id, downloadAttachmentErrorText)}
      />
    </Tooltip>
  );
};

const EmptyState = () => {
  return (
    <>
      <FormattedMessage id="project.tasks.attachments.empty.state.message" />
    </>
  );
};

export const ProjectTaskAttachmentsGrid = ({
  projectId: [_, _projectId],
  searchInput,
  taskAttachments,
}: {
  projectId: ProjectId;
  searchInput: string;
  taskAttachments?: ProjectTaskAttachments[];
}) => {
  const addedByText = useTranslation('added_by');
  const createdOnText = useTranslation('created_on');
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [count, setCount] = useState(0);
  const [searchCount, setSearchCount] = useState<number | null>(null);
  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);
  const getUserName = useUserNameGetter();

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'name',
        flex: 1,
        cellRenderer: 'fileNameRenderer',
      },
      {
        field: 'source',
        flex: 1,
        cellRenderer: 'taskCellRenderer',
      },
      {
        field: 'added_by',
        width: 200,
        headerName: addedByText,
      },
      {
        field: 'created_on',
        width: 200,
        headerName: createdOnText,
      },
      {
        width: 48,
        cellClass: 'file-more-options',
        cellRenderer: 'downloadButtonRenderer',
        sortable: false,
      },
    ],
    [addedByText, createdOnText],
  );
  const [rowData, setRowData] = useState<any[]>([]);

  useEffect(() => {
    const _rowData: Row[] = [];

    taskAttachments?.forEach((_task) => {
      _rowData.push(
        ..._task.attachments.map((_file) => ({
          id: _file.attachmentId,
          name: _file.name,
          source: _task.taskName,
          taskId: _task.taskId,
          projectId: _projectId,
          added_by: getUserName(_file.createdBy),
          created_on: moment(_file.createdAt).format('MMM DD, YYYY'),
          isPrivate: _task.isPrivate,
          location: _file.location,
          versionMappingId: _file.versionMappingId,
          latestVersion: _file.latestVersion,
          commentThreadId: _file?.commentThreadId,
          isAnnotationStarted: _file?.isAnnotationStarted,
        })),
      );
    });
    setRowData(_rowData);
  }, [_projectId, getUserName, taskAttachments]);

  useEffect(() => {
    setCount(rowData.length);
  }, [rowData.length]);

  useEffect(() => {
    gridApi?.setQuickFilter(searchInput);

    setSearchCount(searchInput ? gridApi?.getDisplayedRowCount() || 0 : null);
  }, [gridApi, searchInput, rowData]);

  const frameworkComponents = useMemo(
    () => ({
      taskCellRenderer: memo(TaskCellRenderer),
      fileNameRenderer: memo(FileNameRenderer),
      downloadButtonRenderer: memo(DownloadButtonRenderer),
      EmptyState,
    }),
    [],
  );

  const defaultColumnDef = useMemo(
    () => ({
      suppressMenu: true,
      sortable: true,
    }),
    [],
  );

  const getRowStyle = useCallback((params) => {
    if (params.data.isPrivate) {
      return { background: 'var(--yellow-10)' };
    }

    return {};
  }, []);

  return (
    <GridWrapper rowCount={searchCount} data-cy="Attachments from tasks">
      <GridCollapse
        defaultActiveKey="task-attachments"
        expandIcon={() => <></>}
      >
        <Panel
          key="task-attachments"
          header={
            <Title>
              <Attachment16 />
              <FormattedMessage
                id="project.task.attachments.title"
                values={{
                  count: searchCount || count,
                }}
              />
            </Title>
          }
        >
          <div>
            <StyledGrid
              columnDefs={columnDefs}
              rowData={rowData}
              className="ag-theme-balham"
              animateRows
              suppressContextMenu
              suppressCellSelection
              suppressHorizontalScroll
              onGridReady={onGridReady}
              domLayout="autoHeight"
              noRowsOverlayComponent="EmptyState"
              defaultColDef={defaultColumnDef}
              frameworkComponents={frameworkComponents}
              getRowStyle={getRowStyle}
            />
          </div>
        </Panel>
      </GridCollapse>
    </GridWrapper>
  );
};
